import React from "react";
import { useMutation } from "react-query";
import useAuth from "../hooks/useAuth";
import axiosInstance from "../utils/ApiUtils/axiosInstance";
import Spinner from "../components/ui/Spinner";
import { AxiosError } from "axios";
import ErrorUtils from "../utils/ApiUtils/ErrorUtils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { AuthenticationResponse } from "../model/AuthenticationResponse";
import { toast } from "react-toastify";
import Icon from "../components/ui/Icon";
import AppVersion from "../components/ui/AppVersion";
import { useLocation } from "react-router-dom";

const loginSchema = Yup.object({
  email: Yup.string().email().required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(4, "Password must be at least 4 characters long")
    .max(32, "Password must not exceed 32 characters long"),
});

// Define form data type
type LoginFormInputs = {
  email: string;
  password: string;
};

/**
 * Contenu de la page de login
 * @returns
 */
export default function PageLogin() {
  const auth = useAuth();
  const location = useLocation();
  const error = location.state?.error;

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = React.useCallback(
    async (formData: LoginFormInputs) => {
      console.debug("Submitting form Data : ", { formData });
      loginMutation.mutate({ email: formData.email, password: formData.password });
      reset();
    },
    [reset]
  );

  const loginMutation = useMutation(
    (params: LoginFormInputs) =>
      axiosInstance.post<ApiResponse<AuthenticationResponse>>("/api/auth", params).then((response) => response.data),
    {
      onSuccess: (resp: ApiResponse<AuthenticationResponse>) => {
        const { data } = resp;
        if (data && data.username && data.token && data.roles) {
          auth.loginAction({
            newLogin: data.username,
            token: data.token,
            roles: data.roles,
          });
        } else {
          console.error("Login failed: missing data, username, or token or roles", data);
          toast.error("Error during authentication");
        }
      },
      onError: (error: AxiosError) => {
        if (error.response && error.response.data) {
          const responseData = error.response.data as ApiResponse<unknown>;
          const apiErrors = responseData.errors;
          console.error("Error query:", apiErrors);
        } else {
          console.error("Unexpected error:", error.message);
        }
      },
    }
  );

  return (
    <div className="bg-forterro h-screen flex flex-col relative">
      <AppVersion tone="dark" className="absolute bottom-4 right-6" />
      <div className="flex flex-col pt-20  gap-14">
        <div className="w-full flex justify-start items-center pl-20">
          <div className="flex gap-4 items-center">
            <img className="w-16" src="/assets/Forterro_Arrow-On-Colour.png" alt="Company Logo" />

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white text-center">
              Sylob Translation
            </h1>
          </div>
        </div>

        <div className="w-full flex items-center justify-center">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-transparent p-4 space-y-4 w-full max-w-md mx-auto font-display"
          >
            <label className="font-display font-bold text-xl text-[#21B7B3] block text-center">
              Sign in to your account
            </label>

            {loginMutation.isError && (
              <div className="error-texte text-lg text-center text-red-500">
                Unable to connect: {ErrorUtils.getErrorMessage(loginMutation.error)}
              </div>
            )}

            {error && <div className="error-texte text-lg text-center text-red-500">{error}</div>}

            {/* Email Input */}
            <div className="w-full flex flex-col items-start space-y-2">
              {errors.email && <span className="error-texte text-lg text-red-500">{errors.email.message}</span>}
              <label htmlFor="login" className="text-white text-sm self-start">
                Email
              </label>
              <input
                className="custom-input focus:outline-none focus:ring-2 focus:ring-[#21B7B3] "
                autoComplete="username"
                placeholder="Enter your Email"
                {...register("email")}
              />
            </div>

            {/* Password Input */}
            <div className="w-full flex flex-col items-start space-y-2">
              {errors.password && <span className="error-texte text-lg text-red-500">{errors.password.message}</span>}
              <label htmlFor="password" className="text-white text-sm self-start">
                Password:
              </label>
              <input
                className="custom-input focus:outline-none focus:ring-2 focus:ring-[#21B7B3]"
                type="password"
                autoComplete="password"
                placeholder="Enter your password"
                {...register("password")}
              />
            </div>

            {/* Button */}
            <button
              className="text-white text-lg flex items-center justify-center py-3 px-6 rounded-lg w-full bg-[#21B7B3] hover:bg-[#17A2A1]"
              type="submit"
              disabled={loginMutation.isLoading}
            >
              {loginMutation.isLoading ? (
                <Spinner legend="Logging in..." />
              ) : (
                <>
                  <Icon icon="desktop" />
                  <div className="ml-2">Sign In</div>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
